import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'

interface Props {
  body: string
  /** Send the assembled answer text as the user's next turn. Form cards
   *  use Submit-to-send (not stage-to-composer) because the card itself
   *  is the review surface — the user has already seen each picked pill
   *  before clicking Submit. */
  onSend: (text: string) => void
  /** True only for the most-recent assistant turn while no stream is in
   *  flight. Historical forms render their question/answer layout but
   *  are inert; chosen state is recovered from the next user turn when
   *  possible. */
  isLive: boolean
  /** Content of the user turn immediately after this assistant turn, if
   *  any. Used to recover historical answers across page reloads. */
  nextUserContent?: string
}

interface ParsedOption {
  label: string
  isDefault: boolean
  rationale: string | null
}

interface Section {
  label: string
  required: boolean
  /** `options` = button row + per-section "+ other" input (default).
   *  `freeform` = textarea pre-filled with the section's `> draft` lines,
   *  for agent-proposed text the user can edit before submitting. */
  kind: 'options' | 'freeform'
  options: ParsedOption[]
  draft?: string
  placeholder?: string
}

/** Parse one `- ...` line from a concierge-form / concierge-options block.
 *  Mirrors the parser in ConciergeOptionCard.tsx and the plugin's
 *  parseConciergeOption helper. */
function parseOption(raw: string): ParsedOption {
  let line = raw.trim()
  let rationale: string | null = null
  const dashIdx = line.indexOf(' — ')
  if (dashIdx >= 0) {
    rationale = line.slice(dashIdx + 3).trim() || null
    line = line.slice(0, dashIdx).trim()
  }
  let isDefault = false
  const m = line.match(/^(.*?)\s*\(default\)\s*$/i)
  if (m) {
    isDefault = true
    line = m[1].trim()
  }
  return { label: line, isDefault, rationale }
}

/**
 * Multi-question form card. Replaces the default code-block rendering for
 *
 *     ```concierge-form
 *     [Output type] required:true
 *     - A decision made
 *     - An action list
 *     placeholder: or describe the Output yourself
 *
 *     [Date]
 *     - Today
 *     - Tomorrow
 *     - Friday
 *     ```
 *
 * Each `[Question]` section gets its own button row + per-question
 * freeform input. Required sections gate the Submit button. Submitting
 * sends ONE user turn whose body is a labeled "Q: A" list, so the agent
 * sees all answers in one round-trip.
 *
 * Syntax notes:
 *   - Section header: `[Label]`, with optional trailing flags after the
 *     closing bracket — e.g. `[Date] required:true`.
 *   - Option line: `- Option text` (ASCII hyphen + space; em-dashes
 *     fail the match, matching the plain options card).
 *   - Per-question freeform placeholder: `placeholder: hint text…`
 *   - Section bodies are separated by blank lines (optional — the parser
 *     restarts on every `[...]` header anyway).
 */
export function ConciergeFormCard({
  body,
  onSend,
  isLive,
  nextUserContent,
}: Props) {
  const sections = useMemo(() => parseFormBody(body), [body])

  // Recover historical answers from the next user turn. The submit
  // payload format below ("Output type: A · Date: B") is deterministic
  // so we can round-trip it by parsing the same `<label>: <value>`
  // pairs back out.
  const historicalAnswers = useMemo(
    () => parseFormSubmission(nextUserContent, sections),
    [nextUserContent, sections]
  )

  // Local state: per-section chosen option (or freeform string). Starts
  // from historical answers so a page reload re-renders the same picks.
  // Freeform sections additionally seed from their `> draft` content so
  // "submit without editing" sends the agent's proposed draft as-is.
  const initialPicks = useMemo(() => {
    const out: Record<string, string> = {}
    for (const s of sections) {
      if (s.kind === 'freeform' && s.draft) out[s.label] = s.draft
    }
    return { ...out, ...historicalAnswers }
  }, [sections, historicalAnswers])
  const [picks, setPicks] = useState<Record<string, string>>(initialPicks)
  const [freeformOpen, setFreeformOpen] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(
    () => Object.keys(historicalAnswers).length === sections.length
  )

  if (sections.length === 0) {
    // Malformed — render the raw body so the agent's intent isn't swallowed.
    return (
      <pre className="concierge-option-card-fallback">
        <code>{body}</code>
      </pre>
    )
  }

  const interactive = isLive && !submitted

  const requireds = sections.filter((s) => s.required).map((s) => s.label)
  const requiredsAnswered = requireds.every(
    (label) => picks[label] && picks[label].trim().length > 0
  )

  const pickOption = (sectionLabel: string, value: string) => {
    if (!interactive) return
    setPicks((cur) => ({ ...cur, [sectionLabel]: value }))
  }

  const setFreeform = (sectionLabel: string, value: string) => {
    if (!interactive) return
    setPicks((cur) => ({ ...cur, [sectionLabel]: value }))
  }

  const handleSubmit = () => {
    if (!interactive || !requiredsAnswered) return
    const lines = sections
      .map((s) => {
        const v = picks[s.label]
        if (!v || !v.trim()) return null
        return `${s.label}: ${v.trim()}`
      })
      .filter(Boolean) as string[]
    if (!lines.length) return
    setSubmitted(true)
    onSend(lines.join('\n'))
  }

  return (
    <div
      className="concierge-form-card"
      aria-disabled={!interactive}
      data-state={submitted ? 'answered' : isLive ? 'live' : 'frozen'}
    >
      {sections.map((section) => {
        const chosen = picks[section.label]
        const sectionHasEnhanced =
          section.kind === 'options' &&
          section.options.some((o) => o.isDefault || o.rationale)
        const chosenIsOption =
          section.kind === 'options' &&
          chosen &&
          section.options.some((o) => o.label === chosen)
            ? chosen
            : null
        const chosenIsFreeform =
          section.kind === 'options' && chosen && !chosenIsOption
            ? chosen
            : null
        const showFreeform =
          freeformOpen[section.label] || chosenIsFreeform !== null
        return (
          <div
            key={section.label}
            className="concierge-form-section"
            data-required={section.required ? 'true' : 'false'}
            data-kind={section.kind}
          >
            <div className="concierge-form-question">
              <span className="concierge-form-question-label">
                {section.label}
              </span>
              {section.required && (
                <span
                  className="concierge-form-required-dot"
                  title="Required"
                  aria-label="Required"
                >
                  *
                </span>
              )}
            </div>
            {section.kind === 'freeform' ? (
              <textarea
                className="concierge-form-textarea"
                placeholder={section.placeholder || 'edit before submitting'}
                value={chosen ?? ''}
                onChange={(e) => setFreeform(section.label, e.target.value)}
                disabled={!interactive}
                rows={Math.max(2, Math.min(8, (chosen ?? '').split('\n').length))}
              />
            ) : (
              <>
                <div
                  className="concierge-option-row"
                  data-layout={sectionHasEnhanced ? 'stack' : 'pill'}
                >
                  {section.options.map((opt) => {
                    const isChosen = chosenIsOption === opt.label
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        className="concierge-option-btn"
                        data-chosen={isChosen ? 'true' : 'false'}
                        data-default={opt.isDefault ? 'true' : undefined}
                        disabled={!interactive}
                        onClick={() => pickOption(section.label, opt.label)}
                      >
                        <span className="concierge-option-label">
                          {isChosen && (
                            <Check
                              size={12}
                              strokeWidth={2.5}
                              className="concierge-option-check"
                              aria-hidden
                            />
                          )}
                          <span>{opt.label}</span>
                          {opt.isDefault && (
                            <span className="concierge-option-default-tag">
                              default
                            </span>
                          )}
                        </span>
                        {opt.rationale && (
                          <span className="concierge-option-rationale">
                            {opt.rationale}
                          </span>
                        )}
                      </button>
                    )
                  })}
                  {interactive && !showFreeform && (
                    <button
                      type="button"
                      className="concierge-option-btn concierge-option-btn-ghost"
                      onClick={() =>
                        setFreeformOpen((cur) => ({
                          ...cur,
                          [section.label]: true,
                        }))
                      }
                    >
                      + other
                    </button>
                  )}
                </div>
                {showFreeform && (
                  <input
                    type="text"
                    className="concierge-option-input"
                    placeholder={section.placeholder || 'or type a different answer'}
                    value={chosenIsFreeform ?? ''}
                    onChange={(e) => setFreeform(section.label, e.target.value)}
                    disabled={!interactive}
                  />
                )}
              </>
            )}
          </div>
        )
      })}
      <div className="concierge-form-actions">
        <button
          type="button"
          className="concierge-form-submit"
          disabled={!interactive || !requiredsAnswered}
          onClick={handleSubmit}
        >
          {submitted ? 'Sent' : 'Submit answers'}
        </button>
        {!requiredsAnswered && interactive && (
          <span className="concierge-form-hint">
            answer {requireds.filter((l) => !picks[l]).length} required
          </span>
        )}
      </div>
    </div>
  )
}

function parseFormBody(body: string): Section[] {
  const lines = body.split('\n')
  const sections: Section[] = []
  let cur: Section | null = null
  const draftLines: string[] = []
  const flushDraft = () => {
    if (cur && cur.kind === 'freeform' && draftLines.length) {
      cur.draft = draftLines.join('\n')
    }
    draftLines.length = 0
  }
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    const header = line.match(/^\[(.+?)\](.*)$/)
    if (header) {
      flushDraft()
      if (cur) sections.push(cur)
      const flags = header[2] || ''
      const isFreeform = /\bfreeform\s*:\s*true\b/i.test(flags)
      cur = {
        label: header[1].trim(),
        required: /\brequired\s*:\s*true\b/i.test(flags),
        kind: isFreeform ? 'freeform' : 'options',
        options: [],
      }
      continue
    }
    if (!cur) continue
    if (cur.kind === 'freeform' && line.startsWith('>')) {
      // `> text` inside a freeform section becomes the textarea pre-fill.
      // Multiple `>` lines join with newlines so multi-paragraph drafts work.
      draftLines.push(line.replace(/^>\s?/, ''))
      continue
    }
    if (line.startsWith('-')) {
      const opt = line.replace(/^-\s*/, '').trim()
      if (opt && cur.kind === 'options') cur.options.push(parseOption(opt))
      continue
    }
    const ph = line.match(/^placeholder\s*:\s*(.+)$/i)
    if (ph) {
      cur.placeholder = ph[1].trim()
      continue
    }
  }
  flushDraft()
  if (cur) sections.push(cur)
  // Drop sections that wouldn't render usefully: options-kind with no
  // options, or freeform-kind with neither a draft nor a placeholder.
  return sections.filter((s) =>
    s.kind === 'options' ? s.options.length > 0 : Boolean(s.draft || s.placeholder)
  )
}

function parseFormSubmission(
  text: string | undefined,
  sections: Section[]
): Record<string, string> {
  if (!text) return {}
  const out: Record<string, string> = {}
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  for (const line of lines) {
    const m = line.match(/^([^:]+):\s*(.+)$/)
    if (!m) continue
    const label = m[1].trim()
    const value = m[2].trim()
    if (sections.find((s) => s.label === label)) {
      out[label] = value
    }
  }
  return out
}
